import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  accordionStyle,
  accordionSummaryStyle,
  accordionDetailsStyle,
} from "./styles";
import { tokens, withAlpha } from "../../Common/designTokens";
import { formatPoolFactor } from "../../Utils/poolFactorFormatter";

const bondInfoBoxStyle = {
  bgcolor: tokens.colors.defaultGridRowColor,
  borderRadius: "8px",
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  px: 1,
  py: 0.5,
};

const bondInfoLabelStyle = {
  color: tokens.colors.fontColour85,
  mb: 0.1,
};

const bondInfoValueStyle = {
  color: tokens.colors.fontColour,
};

const getBondCalcLocalTimestamp = () => {
  const now = new Date();
  const pad = (value, length = 2) => String(value).padStart(length, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`;
};

const logBondCalcDebug = (stage, payload) => {
  console.log("debugging-bond-calc", `[${getBondCalcLocalTimestamp()}] ${stage}`, payload);
};

const BondDetailsPanel = ({
  source,
  bondDetails,
  bondCalcResults,
  calcLoading,
  cusipError,
  isLoading,
  actualOrderData,
  calcErrorMessage,
  isTradeEntry, // Add this prop to conditionally hide certain fields
  isZeroPositionEntry, // NEW: hide certain calc fields for zero position entries
}) => {
  const [expanded, setExpanded] = useState(false);
  const lastRenderedBondCalcKeyRef = useRef(null);
  const isAllocate = source === "allocateAccount";

  const handleAccordionChange = (_, isExpanded) => setExpanded(isExpanded);

  useEffect(() => {
    if (calcLoading || !bondCalcResults) return;

    const renderKey = bondCalcResults?.UUID || JSON.stringify({
      cusip: actualOrderData?.cusip,
      settlementDate: bondCalcResults?.Settlement_Date,
      yield: bondCalcResults?.Yield,
      updatedAt: bondCalcResults?.updatedAt,
    });

    if (lastRenderedBondCalcKeyRef.current === renderKey) return;
    lastRenderedBondCalcKeyRef.current = renderKey;

    logBondCalcDebug("ui-rendered BondDetailsPanel", {
      UUID: bondCalcResults?.UUID,
      cusip: actualOrderData?.cusip,
      settlementDate: bondCalcResults?.Settlement_Date,
    });
  }, [bondCalcResults, calcLoading, actualOrderData?.cusip]);

  // Helper: consider only null/undefined as "missing"
  const isDefined = (v) => v !== null && v !== undefined;

  // Helper: treat numbers or numeric strings as finite numbers
  const isFiniteNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n);
  };

  // Parse numbers but preserve empty string as "not provided" (undefined)
  const parseNumberOrUndefined = (v) => {
    if (!isDefined(v) || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const formatCurrency = (value) => {
    // show '---' only for null/undefined or non-finite values
    if (!isDefined(value)) return "---";
    const num = Number(value);
    if (!Number.isFinite(num)) return "---";
    return `$${num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDateToMMDDYYYY = (dateValue) => {
    if (!dateValue) return "---";

    const parseDate = (value) => {
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      const valueStr = String(value).trim();
      const ymd = /^\s*(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s].*)?\s*$/;
      const ymdCompact = /^\s*(\d{4})(\d{2})(\d{2})\s*$/;
      const mdy = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[T\s].*)?\s*$/;
      let match;

      if ((match = valueStr.match(ymd)) || (match = valueStr.match(ymdCompact))) {
        return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
      }

      if ((match = valueStr.match(mdy))) {
        return new Date(Date.UTC(Number(match[3]), Number(match[1]) - 1, Number(match[2])));
      }

      const normalized = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(valueStr) ? valueStr : `${valueStr}Z`;
      const parsed = new Date(normalized);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const date = parseDate(dateValue);
    if (!date) return "---";

    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");
    const yyyy = date.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const getAllBondFields = () => {
    if (!bondDetails) return [];
    const secMaster = bondDetails.secMasterDTO;
    const source = secMaster ;

    const normalized = {
      cusip: source.identifier,
      description: source.description ?? source.securityDesc,
      ratingSnp: source.ratingSnp || source.spRating,
      ratingMoody: source.ratingMoody || source.moodyRating,
      coupon: source.couponPer ?? source.coupon,
      maturity: source.maturity ?? source.maturityDate,
      zeroCoupon: source.isZeroCoupon,
      callable: source.isCallable,
      bondType: source.bondType,
      conventionMethod: source.conventionMethod,
      subType: source.subType || source.bondSubType,
      govState: source.govState || source.state,
      incomeSource: source.incomeSource,
      underlyingIssuerRating: source.underlyingIssuerRating,
      sector: source.sector,
      securityCharge: source.securityCharge,
      poolFactor: source.poolFactor,
    };

    const mainFields = [
      { label: "Identifier", value: normalized.cusip == null ? "---" : normalized.cusip },
      { label: "Short Description", value: normalized.description == null ? "---" : normalized.description },
      {
        label: "Ratings - S&P/Moody's",
        value: 
          (normalized.ratingSnp == null || normalized.ratingSnp === "" ? "---" : normalized.ratingSnp) + "/"+
          (normalized.ratingMoody == null || normalized.ratingMoody === "" ? "---" : normalized.ratingMoody)
      },
      { label: "Coupon", value: normalized.coupon == null ? "---" : normalized.coupon },
      { label: "Maturity", value: normalized.maturity == null ? "---" : formatDateToMMDDYYYY(normalized.maturity) },
      { 
        label: "Zero Coupon", 
        value: normalized.zeroCoupon == null 
          ? "---" 
          : normalized.zeroCoupon 
      },
      { label: "Callable", value: normalized.callable == null ? "---" : normalized.callable },
      { label: "Bond Type", value: normalized.bondType == null ? "---" : normalized.bondType },
      { label: "Convention Method", value: normalized.conventionMethod == null ? "---" : normalized.conventionMethod },
      { label: "Bond Sub Type", value: normalized.subType == null ? "---" : normalized.subType },
    ];

    // Normalize bondType for comparison
    const bondTypeLower = (normalized.bondType || "").toLowerCase();

    if (bondTypeLower === "muni") {
      mainFields.push(
        { label: "Gov State", value: normalized.govState == null ? "---" : normalized.govState },
        { label: "Income Source", value: normalized.incomeSource == null ? "---" : normalized.incomeSource },
        { label: "Underlying Issuer Rating", value: normalized.underlyingIssuerRating == null ? "---" : normalized.underlyingIssuerRating }
      );
    } else if (bondTypeLower === "corp") {
      mainFields.push({ label: "Sector", value: normalized.sector == null ? "---" : normalized.sector });
    }

    mainFields.push({ label: "Factored Securities", value: normalized.securityCharge == null ? "---" : normalized.securityCharge });

    if (normalized.securityCharge != null) {
      mainFields.push({ label: "Factor", value: formatPoolFactor(normalized.poolFactor) });
    }

    return mainFields;
  };

  const getAdditionalFields = () => {
    if (!bondDetails) return [];
    const secMaster = bondDetails.secMasterDTO || {};
    return [
      { label: "Issue Date", value: formatDateToMMDDYYYY(secMaster.issueDate) },
      { label: "Coupon Frequency", value: secMaster.interestPaymentFrequency || "---" },
      { label: "Coupon Type", value: secMaster.couponType || "---" },
      { label: "First Coupon Date", value: formatDateToMMDDYYYY(secMaster.firstCouponDate) },
      { label: "Last Coupon Date", value: formatDateToMMDDYYYY(secMaster.lastCouponDate) },
    ];
  };

  const renderBondCalculations = () => {
    // NEW: Check if MANUAL_TRADED in read-only mode
    const isManualTraded = actualOrderData?.rfqStatus === "MANUAL_TRADED" ;
    
    if (calcErrorMessage) {
      // For MANUAL_TRADED, show friendly message instead of error
      if (isManualTraded) {
        return (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 1.5, color: tokens.colors.fontColour, fontSize: '1rem' }}>
              Bond Calculations
            </Typography>
            <Box sx={{
              p: 2,
              bgcolor: tokens.colors.defaultGridRowColor,
              borderRadius: '8px',
              border: `1px solid ${tokens.colors.bodyBorderColor50}`,
              color: tokens.colors.fontColour85,
            }}>
              <Typography>
                Bond calculation data is not availabler.
              </Typography>
            </Box>
          </Box>
        );
      }
      
      return (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 1.5, color: tokens.colors.fontColour, fontSize: '1rem' }}>
            Bond Calculations
          </Typography>
          <Box sx={{
            p: 2,
            bgcolor: tokens.colors.defaultGridRowColor,
            borderRadius: '8px',
            border: `1px solid ${tokens.palette.danger}`,
            color: tokens.palette.danger,
          }}>
            <Typography>
              {
                // NEW: If the message indicates maturity, show a tailored message
                /mature/i.test(calcErrorMessage)
                  ? "Bond calculations are not available because this security has already matured."
                  : (
                      calcErrorMessage.includes("not available") || 
                      calcErrorMessage.includes("not found") || 
                      calcErrorMessage.includes("security master") || 
                      calcErrorMessage.includes("CUSIP") ||
                      calcErrorMessage.includes("identifier")
                        ? "Bond calculations are not available for this security. The security may not be in our database."
                        : calcErrorMessage
                    )
              }
            </Typography>
          </Box>
        </Box>
      );
    }

    if (!bondCalcResults && !calcLoading) return null;

    if (calcLoading) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 1.5, color: tokens.colors.fontColour, fontSize: '1rem' }}>
            Bond Calculations
          </Typography>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            bgcolor: tokens.colors.defaultGridRowColor,
            borderRadius: '8px',
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            minHeight: '100px'
          }}>
            <CircularProgress size={24} sx={{ color: tokens.palette.primary, mr: 2 }} />
            <Typography sx={{ color: tokens.colors.fontColour85 }}>
              Calculating bond values...
            </Typography>
          </Box>
        </Box>
      );
    }

    // Parse qty/price/accruedInterest but keep "" as undefined so it doesn't become 0
    const qty = parseNumberOrUndefined(actualOrderData?.qty);
    const price = parseNumberOrUndefined(actualOrderData?.price);
    const accruedInterest = parseNumberOrUndefined(bondCalcResults?.Accr_Interest);

    const canCompute = Number.isFinite(qty) && Number.isFinite(price);

    // If both securityCharge (factored securities) and poolFactor are non-null, apply pool factor to principal
    const secMaster = bondDetails?.secMasterDTO;
    const rawPoolFactor = secMaster?.poolFactor;
    const rawSecurityCharge = secMaster?.securityCharge;
    const poolFactorMultiplier =
      rawSecurityCharge != null && rawPoolFactor != null
        ? parseNumberOrUndefined(rawPoolFactor) ?? 1
        : 1;

    // Principal always computed when qty & price are provided (0 is valid)
    const principalAmount = canCompute ? formatCurrency((price * qty * poolFactorMultiplier) / 100) : "---";

    // Accrued interest displayed only when both accruedInterest and qty are valid numbers
    const accruedInterestAmount = (Number.isFinite(accruedInterest) && Number.isFinite(qty))
      ? formatCurrency((accruedInterest * qty) / 100)
      : "---";

    // Estimated total: principal + accrued interest (if accrued interest is available)
    let estimatedTotal = "---";
    if (canCompute) {
      const principal = (price * qty * poolFactorMultiplier) / 100;
      const aiPart = Number.isFinite(accruedInterest) ? (accruedInterest * qty) / 100 : 0;
      estimatedTotal = formatCurrency(principal + aiPart);
    }

    // Helper to safely format calc result fields with 6 decimals if numeric
    const fmt6 = (val) => {
      if (!isDefined(val)) return "---";
      const n = Number(val);
      return Number.isFinite(n) ? n.toFixed(6) : "---";
    };

    const calcFields = [
      // Conditionally include principal, accrued interest, and estimated total only if not from trade entry
      ...( (isTradeEntry || isZeroPositionEntry) ? [] : [
        { label: "Principal Amount", value: principalAmount },
        { label: "Accrued Interest", value: accruedInterestAmount },
        { label: "Estimated Total", value: estimatedTotal },
      ]),
      { label: "Dollar Convexity", value: fmt6(bondCalcResults?.Dollar_Convexity) },
      { label: "DV01", value: fmt6(bondCalcResults?.DV01) },
      { label: "$ Duration", value: fmt6(bondCalcResults?.Duration) },
      { label: "Macaulay Duration", value: fmt6(bondCalcResults?.Macaulay_Duration) },
      { label: "Modified Duration", value: fmt6(bondCalcResults?.Modified_Duration) },
      { label: "Modified Convexity", value: fmt6(bondCalcResults?.Modified_Convexity) },
      { label: "Yield", value: fmt6(bondCalcResults?.Yield) },
      // Only include YieldValuePP when NOT a zero position entry
      ...(isZeroPositionEntry ? [] : [{ label: "YieldValuePP", value: fmt6(bondCalcResults?.YieldValuePP) }]),
      { label: "YieldToWorst", value: fmt6(bondCalcResults?.YieldToWorst) },
    ];

    return (
      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 1.5, color: tokens.colors.fontColour, fontSize: '1rem' }}>
          Bond Calculations
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {calcFields.map((field, index) => (
            <Box key={index} sx={bondInfoBoxStyle}>
              <Typography variant="caption" sx={bondInfoLabelStyle}>
                {field.label}
              </Typography>
              <Typography variant="body2" sx={bondInfoValueStyle}>
                {field.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {bondDetails && bondDetails.secMasterDTO == null ? (
        <Box sx={{
          mt: 2,
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
        }}>
          <Typography sx={{ fontWeight: 'normal', color: tokens.colors.fontColour }}>
            {actualOrderData?.rfqStatus === "MANUAL_TRADED" 
              ? "Bond data is not available for this security."
              : "Bond data is not available for this security."}
          </Typography>
        </Box>
      ) : (
        <>
          {bondDetails && (
            <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(isTradeEntry 
                ? [...getAllBondFields(), ...getAdditionalFields()]
                : getAllBondFields()
              ).map((field, index) => (
                <Box key={index} sx={bondInfoBoxStyle}>
                  <Typography variant="caption" sx={bondInfoLabelStyle}>
                    {field.label}
                  </Typography>
                  <Typography variant="body2" sx={bondInfoValueStyle}>
                    {field.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {bondDetails && !isTradeEntry && (
            <Accordion expanded={expanded} onChange={handleAccordionChange} sx={accordionStyle}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyle}>
                <Typography sx={{ color: tokens.colors.fontColour }}>Additional Information</Typography>
              </AccordionSummary>
              <AccordionDetails sx={accordionDetailsStyle}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {getAdditionalFields().map((field, index) => (
                    <Box key={index} sx={bondInfoBoxStyle}>
                      <Typography variant="caption" sx={bondInfoLabelStyle}>
                        {field.label}
                      </Typography>
                      <Typography variant="body2" sx={bondInfoValueStyle}>
                        {field.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}

      {!bondDetails && actualOrderData.cusip && !isLoading && (
        <Box sx={{
          mt: 2,
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
          backgroundColor: (cusipError && actualOrderData?.rfqStatus !== "MANUAL_TRADED") ? withAlpha(tokens.palette.danger, 0.133) : tokens.colors.defaultGridRowColor,
          color: (cusipError && actualOrderData?.rfqStatus !== "MANUAL_TRADED") ? tokens.palette.danger : tokens.colors.fontColour85
        }}>
          <Typography sx={{ 
            fontWeight: (cusipError && actualOrderData?.rfqStatus !== "MANUAL_TRADED") ? 'bold' : 'normal', 
            color: (cusipError && actualOrderData?.rfqStatus !== "MANUAL_TRADED") ? tokens.palette.danger : tokens.colors.fontColour 
          }}>
            {actualOrderData?.rfqStatus === "MANUAL_TRADED"
              ? "Bond details are not available."
              : (cusipError || "Enter a valid Identifier to see bond details")}
          </Typography>
        </Box>
      )}

      {!bondDetails && !actualOrderData.cusip && cusipError && !isLoading && actualOrderData?.rfqStatus !== "MANUAL_TRADED" && (
        <Box sx={{
          mt: 2,
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${tokens.palette.danger}`,
          backgroundColor: withAlpha(tokens.palette.danger, 0.133),
          color: tokens.palette.danger
        }}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {cusipError}
          </Typography>
        </Box>
      )}

      {renderBondCalculations()}
    </>
  );
};

export default BondDetailsPanel;
